// "Réglages" tab of the agent drawer (manage level): identity, brief (counter 6 000), engine with
// explanations, model with suggestions, effort (Claude), autonomy, web, files, the advanced « Durée
// maximale d’un travail » (timeoutMin, SPEC-EQUIPE §2), connectors among the
// floor's allowed ones and the office library's (added in the office, « tous les étages », with
// who added them) (icon, read / write / none, tool counts, suggested names, not-connected
// warnings, and for a granted server « Outils : tous (N) » or « k sur N outils » with a checklist
// « Choisir les outils » grouped Lecture / Écriture, with a search, fed by GET
// /api/agents/:id/connectors/:server/tools — SPEC-UX-V03 §A.3; a connector outside the member's
// « Connecteurs autorisés » shows locked: kept, narrowed or removed, never given nor widened),
// lessons (immediate add / delete),
// save with PATCH /api/agents/:id (field problems from the 400 details), and the admin-only
// "Supprimer l'agent".
import {
  h, icon, badge, button, input, textarea, field, select, segmented, switchToggle, statusDot, emptyState,
  spinner, iconButton, setText, replaceChildren,
} from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { autosize } from '../tasks/components.js';
import {
  LIMITS, EFFORTS, formFromAgent, diffAgentForm, validateAgentForm, validateLesson, connectorRows, problemsFromDetails,
  modelSuggestions, modelLabel, modelPlaceholderKey, charLength, mergeAgent, EFFORT_ENGINES, NO_CONNECTOR_ENGINES, groupTools, toolsSummary, toolSelection,
  libraryKeysOf,
} from './model.js';
import { iconForConnector } from '../connectoricons.js';
import { canUseConnector } from '../connectorpolicy.js';
import './strings.js';

const ENGINE_OPTIONS = [
  { value: 'claude', key: 'ad.f.engineClaude' },
  { value: 'codex', key: 'ad.f.engineCodex' },
  { value: 'openai-image', key: 'ad.f.engineImages' },
  { value: 'gemini', key: 'ad.f.engineGemini' },
  { value: 'kie', key: 'ad.f.engineKie' },
];
const FIELD_ORDER = ['name', 'role', 'does', 'brief', 'engine', 'model', 'effort', 'autonomy', 'web', 'files', 'connectors', 'timeoutMin'];

/**
 * Replaces the agent in the store's office (so the scene, name tags and lists update at once).
 * @param {object} ctx
 * @param {object} agent the server's copy
 */
export function storeAgent(ctx, agent) {
  if (!agent || typeof agent.id !== 'string') return;
  const office = ctx.store.get().office;
  ctx.store.set({ office: { ...office, agents: mergeAgent(office.agents, agent) } });
}

/**
 * A text control with a live character counter ("123 / 6000", red when over).
 * @param {HTMLInputElement|HTMLTextAreaElement} control
 * @param {number} max
 */
function withCounter(control, max) {
  const counter = h('span', { class: 'ad-count', 'aria-live': 'off' });
  const sync = () => {
    const n = charLength(control.value);
    setText(counter, t('ad.f.count', { n, max }));
    counter.classList.toggle('is-over', n > max);
  };
  control.addEventListener('input', sync);
  sync();
  return { counter, sync };
}

/**
 * Builds the tab.
 * @param {object} ctx app context
 * @param {object} initialAgent the agent as the store knows it (the full copy is fetched)
 * @param {{isAdmin?: boolean, onDeleted?: () => void}} [opts]
 * @returns {{el: HTMLElement, footer: HTMLElement, activate(): void, deactivate(): void, isDirty(): boolean, destroy(): void}}
 */
export function createSettingsTab(ctx, initialAgent, { isAdmin = false, onDeleted } = {}) {
  const id = initialAgent.id;
  const path = `/api/agents/${encodeURIComponent(id)}`;
  let agent = initialAgent;
  let original = formFromAgent(agent);
  let form = structuredClone(original);
  let loaded = false;
  let loading = false;
  let saving = false;
  let destroyed = false;
  let pendingFocus = null;   // a focusField() asked before the agent was loaded
  let pendingSection = null; // a showSection() asked before the agent was loaded
  let changedElsewhere = false;
  const fields = {};        // key → field wrapper (setError)
  const offs = [];

  const body = h('div', { class: 'ad-settings' }, h('div', { class: 'ad-tab-loading' }, spinner(t('ad.settings.loading')), h('span', { class: 'muted' }, t('ad.settings.loading'))));
  const el = h('div', { class: 'ad-settings-wrap' }, body);

  // --- save bar (footer) ------------------------------------------------------------------------
  const status = h('p', { class: 'ad-savebar-status', 'aria-live': 'polite' });
  const general = h('ul', { class: 'ad-savebar-problems', role: 'alert', hidden: true });
  const discardBtn = button(t('ad.discard'), { variant: 'ghost', size: 'sm', onClick: () => discard() });
  const saveLabel = h('span', { class: 'btn-label' }, t('ad.save'));
  const saveBtn = h('button', { type: 'button', class: 'btn btn-primary ad-save', onClick: () => save() }, icon('check', { size: 16 }), saveLabel);
  const footer = h('div', { class: 'ad-savebar' }, h('div', { class: 'ad-savebar-info' }, status, general), discardBtn, saveBtn);

  // The floor's allowlist, and the library's connectors (on every floor).
  const allowedList = () => {
    const state = ctx.store.get();
    const floor = state.office.floors.find((f) => f.id === agent.floorId);
    return Array.isArray(floor?.connectors) ? [...floor.connectors, ...libraryKeysOf(state.connectors)] : null;
  };
  const patch = () => diffAgentForm(original, form, { allowed: allowedList() });

  function setGeneral(list) {
    general.replaceChildren(...list.map((m) => h('li', {}, m)));
    general.hidden = !list.length;
  }

  function sync() {
    if (destroyed) return;
    const n = Object.keys(patch()).length;
    setText(status, changedElsewhere && n ? t('ad.changedElsewhere') : n ? t('ad.dirty', { n }) : t('ad.clean'));
    status.classList.toggle('is-dirty', n > 0);
    saveBtn.disabled = !n || saving || !loaded;
    discardBtn.disabled = !n || saving;
    discardBtn.hidden = !n;
    saveBtn.classList.toggle('is-busy', saving);
    setText(saveLabel, saving ? t('ad.saving') : t('ad.save'));
  }

  function showErrors(errors) {
    for (const [key, f] of Object.entries(fields)) f.setError?.(errors[key] || null);
  }

  // --- sections ---------------------------------------------------------------------------------
  function sectionEl(label, ...children) {
    return h('section', { class: 'ad-set-sec', 'aria-label': label }, h('h3', { class: 'ad-set-title mono-label' }, label), ...children);
  }

  /** A section the Aperçu can open directly (« Modifier les connecteurs »). */
  function namedSection(key, label, ...children) {
    const sec = sectionEl(label, ...children);
    sec.dataset.sec = key;
    sec.setAttribute('tabindex', '-1');
    return sec;
  }

  function identity() {
    const name = input({ value: form.name, maxLength: LIMITS.name + 6, autocomplete: 'off', spellcheck: false, class: 'ad-name-input' });
    name.addEventListener('input', () => { form.name = name.value; fields.name.setError(null); sync(); });
    const role = input({ value: form.role, maxLength: LIMITS.role + 20 });
    role.addEventListener('input', () => { form.role = role.value; fields.role.setError(null); sync(); });
    const does = textarea({ value: form.does, rows: 3 });
    autosize(does, { max: 220, min: 72 });
    const doesCount = withCounter(does, LIMITS.does);
    does.addEventListener('input', () => { form.does = does.value; fields.does.setError(null); sync(); });
    fields.name = field(t('ad.f.name'), name, { hint: t('ad.f.nameHint') });
    fields.role = field(t('ad.f.role'), role, { hint: t('ad.f.roleHint') });
    fields.does = field(t('ad.f.does'), does, { hint: t('ad.f.doesHint') });
    fields.does.appendChild(doesCount.counter);
    return sectionEl(t('ad.set.identity'), h('div', { class: 'ad-grid-2' }, fields.name, fields.role), fields.does);
  }

  function briefSection() {
    const brief = textarea({ value: form.brief, rows: 8, placeholder: t('ad.f.briefPlaceholder'), class: 'ad-brief' });
    const fit = autosize(brief, { max: 520, min: 160 });
    const count = withCounter(brief, LIMITS.brief);
    brief.addEventListener('input', () => { form.brief = brief.value; fields.brief.setError(null); sync(); });
    fields.brief = field(t('ad.f.brief'), brief, { hint: t('ad.f.briefHint') });
    fields.brief.appendChild(count.counter);
    setTimeout(fit, 0);
    return sectionEl(t('ad.set.brief'), fields.brief);
  }

  function engineSection(onEngine) {
    const help = h('p', { class: 'ad-engine-help' });
    const down = h('p', { class: 'ad-warn', role: 'note', hidden: true });
    const modelInput = input({ value: form.model, placeholder: t('ad.f.modelPlaceholder'), autocomplete: 'off', spellcheck: false, maxLength: LIMITS.model + 10 });
    modelInput.addEventListener('input', () => { form.model = modelInput.value; fields.model.setError(null); sync(); });
    const suggest = h('div', { class: 'ad-model-suggest', role: 'group', 'aria-label': t('ad.model.suggestions') });
    fields.model = field(t('ad.f.model'), modelInput, { hint: t('ad.f.modelHint') });
    fields.model.appendChild(suggest);
    const effortSel = select([{ value: '', label: t('ad.f.effortDefault') }, ...EFFORTS.map((e) => ({ value: e, label: t(`ad.f.effort.${e}`) }))], { value: form.effort });
    effortSel.addEventListener('change', () => { form.effort = effortSel.value; sync(); });
    fields.effort = field(t('ad.f.effort'), effortSel, { hint: t('ad.f.effortHint') });
    const seg = segmented(ENGINE_OPTIONS.map((o) => ({ value: o.value, label: t(o.key) })), {
      value: form.engine, label: t('ad.f.engine'),
      onChange: (v) => { form.engine = v; refresh(); sync(); onEngine?.(); },
    });
    const engineField = h('div', { class: 'field' }, h('span', { class: 'field-label', id: `${id}-engine-label` }, t('ad.f.engine')), seg, help, down);
    seg.setAttribute('aria-labelledby', `${id}-engine-label`);
    fields.engine = engineField;
    function refresh() {
      setText(help, t(`ad.f.engineHelp.${form.engine}`));
      modelInput.placeholder = t(modelPlaceholderKey(form.engine));
      const st = ctx.store.get().engines?.[form.engine];
      // Not connected, switched off or broken (not merely being checked, nor unchecked for a network hiccup).
      const bad = Boolean(st) && st.ok === false && !st.pending && st.state !== 'pending' && st.state !== 'unknown';
      down.hidden = !bad || !!ctx.store.get().engines?.mock?.forced;
      const off = st?.state === 'disabled' || st?.enabled === false;
      setText(down, off ? t('ad.f.engineDisabled') : st?.state === 'disconnected' ? t('ad.f.engineOff') : t('ad.f.engineDown', { error: '' }));
      fields.effort.hidden = !EFFORT_ENGINES.includes(form.engine);
      const effortHint = [...(fields.effort.childNodes || [])].find((n) => String(n.getAttribute?.('class') || '').includes('field-hint'));
      if (effortHint) setText(effortHint, t(form.engine === 'gemini' ? 'ad.f.effortHintGemini' : 'ad.f.effortHint'));
      suggest.replaceChildren(h('span', { class: 'ad-suggest-label' }, t('ad.conn.suggestions')),
        ...['', ...modelSuggestions(form.engine)].map((m) => {
          // A Kie.ai id says little (« wan/2-7-r2v »): the chip shows its short French name, the id in its tooltip.
          const label = modelLabel(m);
          return h('button', {
            type: 'button', class: ['chip', 'ad-model-chip', { 'is-active': String(form.model).trim() === m }],
            'aria-pressed': String(String(form.model).trim() === m),
            'aria-label': m ? t('ad.model.useSuggestion', { model: label ? `${label} (${m})` : m }) : t('ad.f.modelPlaceholder'),
            ...(label ? { title: m } : {}),
            onClick: () => { modelInput.value = m; form.model = m; fields.model.setError(null); refresh(); sync(); },
          }, h('span', { class: 'chip-label' }, label || m || t('ad.f.modelPlaceholder')));
        }));
    }
    modelInput.addEventListener('input', () => refresh());
    refresh();
    return sectionEl(t('ad.set.engine'), engineField, h('div', { class: 'ad-grid-2' }, fields.model, fields.effort));
  }

  function autonomySection() {
    const hint = h('p', { class: 'ad-autonomy-hint' });
    const seg = segmented([
      { value: 'approve', label: t('ad.f.autonomyApprove'), icon: 'shield' },
      { value: 'auto', label: t('ad.f.autonomyAuto'), icon: 'bolt' },
    ], {
      value: form.autonomy, label: t('ad.f.autonomy'),
      onChange: (v) => { form.autonomy = v; syncHint(); sync(); },
    });
    function syncHint() {
      setText(hint, t(`ad.f.autonomyHint.${form.autonomy}`));
      hint.classList.toggle('is-auto', form.autonomy === 'auto');
    }
    syncHint();
    fields.autonomy = h('div', { class: 'field' }, h('span', { class: 'field-label' }, t('ad.f.autonomy')), seg, hint);
    const web = switchToggle(t('ad.f.web'), { checked: form.web, hint: t('ad.f.webHint'), onChange: (v) => { form.web = !!v; sync(); } });
    const files = switchToggle(t('ad.f.files'), { checked: form.files, hint: t('ad.f.filesHint'), onChange: (v) => { form.files = !!v; sync(); } });
    return sectionEl(t('ad.set.autonomy'), fields.autonomy, h('div', { class: 'ad-switches' }, web, files));
  }

  const connBox = h('div', { class: 'ad-conns' });
  const toolLists = new Map(); // server key → {state: 'loading'|'ready'|'error', tools, error, promise}
  const openPickers = new Set(); // server keys whose tool checklist is open
  const pickerQuery = new Map(); // server key → search text
  const connError = h('p', { class: 'field-error', role: 'alert', hidden: true });
  fields.connectors = { setError: (msg) => { setText(connError, msg || ''); connError.hidden = !msg; } };

  function renderConnectors() {
    const state = ctx.store.get();
    const { office, connectors } = state;
    const floor = office.floors.find((f) => f.id === agent.floorId) || null;
    const rows = connectorRows({
      floor, connectors: connectors || [], access: form.connectors, suggested: agent.suggested || [], engine: form.engine, tools: form.tools,
      canUse: (key) => canUseConnector(state, key),
    });
    const nodes = [h('p', { class: 'ad-conn-intro' }, t('ad.conn.intro', { floor: floor?.name || '' }))];
    if (NO_CONNECTOR_ENGINES.includes(form.engine)) {
      const why = form.engine === 'gemini' ? 'ad.conn.noEngineGemini' : form.engine === 'kie' ? 'ad.conn.noEngineKie' : 'ad.conn.noEngine';
      nodes.push(h('p', { class: 'ad-warn', role: 'note' }, icon('info', { size: 14 }), h('span', {}, t(why))));
    }
    if (!rows.length) {
      nodes.push(h('p', { class: 'ad-notice' }, t('ad.conn.empty')));
    } else {
      nodes.push(h('ul', { class: 'ad-conn-list' }, rows.map((row) => {
        // Allowed: every level. No longer allowed on the floor: removal only. Outside my connectors:
        // kept, narrowed (write → read) or removed, never given nor widened (the server refuses).
        const levels = !row.allowed ? (row.access !== 'none' ? [row.access] : [])
          : row.denied ? ['read', 'write'].slice(0, row.access === 'write' ? 2 : row.access === 'read' ? 1 : 0)
            : ['read', 'write'];
        const options = [{ value: 'none', label: t('ad.conn.none') }, ...levels.map((v) => ({ value: v, label: t(`ad.conn.${v}`) }))];
        const seg = segmented(options, {
          value: row.access, size: 'sm', label: t('ad.conn.accessLabel', { name: row.name }),
          onChange: (v) => {
            const next = { ...form.connectors };
            if (v === 'read' || v === 'write') next[row.key] = v; else delete next[row.key];
            form.connectors = next;
            if (!next[row.key]) {
              // No access: no selection either (a new grant starts with every tool).
              const tools = { ...(form.tools || {}) };
              delete tools[row.key];
              form.tools = tools;
              openPickers.delete(row.key);
            }
            fields.connectors.setError(null);
            sync();
            renderConnectors();
            connBox.querySelector?.(`[data-conn="${CSS_ESCAPE(row.key)}"] .segmented-opt[aria-checked="true"]`)?.focus?.();
          },
        });
        const counts = row.readCount !== null || row.writeCount !== null
          ? h('span', { class: 'ad-conn-tools' }, t('ad.conn.tools', { read: row.readCount ?? 0, write: row.writeCount ?? 0 })) : null;
        const builtin = row.builtin ? h('span', { class: 'ad-conn-tools ad-conn-builtin' }, t('ad.conn.builtin')) : null;
        const warns = [];
        if (row.missing) warns.push(t('ad.conn.missing'));
        else if (!row.connected) {
          const known = ['needs-auth', 'failed', 'added', 'error', 'unknown'].includes(row.status);
          warns.push(t('ad.conn.notConnected', { status: known ? t(`connector.status.${row.status}`) : row.status }));
        }
        if (!row.allowed) warns.push(t('ad.conn.notAllowed'));
        if (row.denied) warns.push(t(row.access === 'none' ? 'ad.conn.deniedNone' : 'ad.conn.denied'));
        if (row.engineMismatch && !NO_CONNECTOR_ENGINES.includes(form.engine) && row.access !== 'none') warns.push(t('ad.conn.engineMismatch'));
        // A server the floor no longer allows is denied whole at run time: no tool line to choose from.
        // One outside my connectors keeps its selection as it is (no widening from here).
        const picker = row.allowed && !row.denied && row.access !== 'none' && !NO_CONNECTOR_ENGINES.includes(form.engine) ? toolsLine(row) : null;
        return h('li', { class: ['ad-conn', { 'is-suggested': row.suggested, 'is-off': !row.connected, 'is-on': row.access !== 'none', 'is-denied': row.denied }], dataset: { conn: row.key } },
          h('div', { class: 'ad-conn-main' },
            iconForConnector(row.server, { size: 28 }),
            h('div', { class: 'ad-conn-info' },
              h('p', { class: 'ad-conn-name' }, statusDot(row.connected ? 'ok' : row.missing ? 'off' : 'warn', row.connected ? null : row.status),
                h('span', {}, row.name),
                row.denied ? badge(t('ad.conn.deniedBadge'), 'amber', { class: 'ad-conn-denied', title: t('ad.conn.deniedTitle') }) : null,
                row.suggested && !row.denied ? badge(t('ad.conn.suggested'), 'amber', { title: t('ad.conn.suggestedTitle') }) : null,
                row.everyFloor ? badge(t('ad.conn.everyFloor'), 'info', { class: 'ad-conn-everyfloor', title: t('ad.conn.everyFloorTitle') }) : null),
              counts,
              row.everyFloor
                ? h('span', { class: 'ad-conn-tools ad-conn-library' }, row.addedByName ? t('ad.conn.libraryBy', { name: row.addedByName }) : t('ad.conn.library'))
                : null,
              builtin,
              ...warns.map((w) => h('p', { class: 'ad-conn-warn' }, icon('alert', { size: 12 }), h('span', {}, w)))),
            seg),
          picker);
      })));
    }
    nodes.push(connError);
    connBox.replaceChildren(...nodes);
  }

  // --- tool picker (a selection of the server's tools per grant) --------------------------------
  /** The tools of a server (cached for the life of the tab; [] when the server does not know them). */
  function loadTools(key) {
    const known = toolLists.get(key);
    if (known && known.state !== 'error') return known.promise;
    const entry = { state: 'loading', tools: [], error: null, promise: null };
    toolLists.set(key, entry);
    entry.promise = ctx.api.get(`${path}/connectors/${encodeURIComponent(key)}/tools`).then((res) => {
      const list = Array.isArray(res) ? res : Array.isArray(res?.tools) ? res.tools : [];
      entry.tools = list.filter((x) => x && typeof x.name === 'string' && x.name);
      entry.state = 'ready';
    }, (err) => {
      if (err?.status === 404) { entry.state = 'ready'; entry.tools = []; return; }
      entry.state = 'error';
      entry.error = err;
    }).then(() => { if (!destroyed && openPickers.has(key)) renderConnectors(); });
    return entry.promise;
  }

  /** « Outils : tous (N) » / « k sur N outils » and the button that opens the checklist. */
  function toolsLine(row) {
    const list = toolLists.get(row.key);
    const total = list?.state === 'ready' && list.tools.length ? list.tools.length : row.toolCount;
    const open = openPickers.has(row.key);
    const summary = h('span', { class: 'ad-conn-toolsum' }, toolsSummary(row.tools, total));
    const panelId = `${id}-tools-${String(row.key).replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const toggle = h('button', {
      type: 'button', class: 'link-btn ad-conn-toolbtn', 'aria-expanded': String(open), 'aria-controls': panelId,
      onClick: () => {
        if (openPickers.has(row.key)) openPickers.delete(row.key);
        else { openPickers.add(row.key); loadTools(row.key); }
        renderConnectors();
        connBox.querySelector?.(`[data-conn="${CSS_ESCAPE(row.key)}"] .ad-conn-toolbtn`)?.focus?.();
      },
    }, icon(open ? 'chevronUp' : 'tool', { size: 13 }), h('span', {}, open ? t('ad.conn.toolsClose') : t('ad.conn.toolsPick')));
    return h('div', { class: 'ad-conn-tools-wrap' },
      h('p', { class: 'ad-conn-toolsline' }, summary, toggle),
      open ? toolPicker(row, panelId, summary) : null);
  }

  /** The checklist of a server's tools, grouped Lecture / Écriture, with a search. */
  function toolPicker(row, panelId, summary) {
    const key = row.key;
    const list = toolLists.get(key);
    const panel = h('div', { class: 'ad-tools', id: panelId, role: 'group', 'aria-label': t('ad.conn.toolsListLabel', { name: row.name }) });
    if (!list || list.state === 'loading') {
      panel.append(h('p', { class: 'ad-tools-note muted' }, spinner(t('ad.conn.toolsLoading')), ' ', t('ad.conn.toolsLoading')));
      return panel;
    }
    if (list.state === 'error') {
      panel.append(h('p', { class: 'ad-conn-warn' }, icon('alert', { size: 12 }), h('span', {}, list.error?.message || t('common.error'))),
        button(t('common.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => { loadTools(key); renderConnectors(); } }));
      return panel;
    }
    if (!list.tools.length) {
      panel.append(h('p', { class: 'ad-tools-note muted' }, t('ad.conn.toolsUnknown')));
      return panel;
    }
    const names = list.tools.map((x) => x.name);
    const ticked = () => new Set(Array.isArray(form.tools?.[key]) ? form.tools[key] : names);
    const groupsEl = h('div', { class: 'ad-tools-groups' });

    function setPick(next) {
      const tools = { ...(form.tools || {}) };
      if (next === null) delete tools[key]; else tools[key] = next;
      form.tools = tools;
      fields.connectors.setError(null);
      setText(summary, toolsSummary(Array.isArray(tools[key]) ? tools[key] : null, names.length));
      sync();
    }

    function fillGroups() {
      const on = ticked();
      const groups = groupTools(list.tools, pickerQuery.get(key) || '');
      const nodes = [];
      for (const kind of ['read', 'write']) {
        const items = groups[kind];
        if (!items.length) continue;
        const all = list.tools.filter((x) => (x.kind === 'write' ? 'write' : 'read') === kind);
        const count = all.filter((x) => on.has(x.name)).length;
        nodes.push(h('fieldset', { class: ['ad-tools-group', `is-${kind}`], dataset: { kind } },
          h('legend', { class: 'ad-tools-legend mono-label' }, kind === 'write' ? t('ad.conn.toolsWrite') : t('ad.conn.toolsRead'),
            h('span', { class: 'ad-tools-count' }, ` ${count}/${all.length}`)),
          kind === 'write' && row.access !== 'write' ? h('p', { class: 'ad-tools-note muted' }, t('ad.conn.toolsWriteNote')) : null,
          h('ul', { class: 'ad-tools-list' }, items.map((tool) => {
            const box = h('input', { type: 'checkbox', class: 'ad-tools-check', value: tool.name, checked: on.has(tool.name) });
            box.addEventListener('change', () => {
              const next = ticked();
              if (box.checked) next.add(tool.name); else next.delete(tool.name);
              setPick(toolSelection(next, names, form.tools?.[key] || null));
              fillGroups();
              [...groupsEl.querySelectorAll('input')].find((b) => b.value === tool.name)?.focus?.();
            });
            return h('li', {}, h('label', { class: 'ad-tools-item' }, box,
              h('span', { class: 'ad-tools-text' }, h('code', { class: 'ad-tools-name' }, tool.name),
                tool.description ? h('span', { class: 'ad-tools-desc muted' }, tool.description) : null)));
          }))));
      }
      groupsEl.replaceChildren(...(nodes.length ? nodes : [h('p', { class: 'ad-tools-note muted' }, t('ad.conn.toolsNoMatch'))]));
    }

    const search = input({ type: 'search', value: pickerQuery.get(key) || '', placeholder: t('ad.conn.toolsSearch'), 'aria-label': t('ad.conn.toolsSearch'), class: 'input-sm ad-tools-search', autocomplete: 'off' });
    search.addEventListener('input', () => { pickerQuery.set(key, search.value); fillGroups(); });
    const every = button(t('ad.conn.toolsEvery'), { variant: 'secondary', size: 'sm', onClick: () => { setPick(null); fillGroups(); } });
    const none = button(t('ad.conn.toolsNoneBtn'), { variant: 'ghost', size: 'sm', onClick: () => { setPick([]); fillGroups(); } });
    fillGroups();
    panel.append(h('p', { class: 'ad-tools-hint' }, t('ad.conn.toolsHint')),
      h('div', { class: 'ad-tools-bar' }, search, every, none), groupsEl);
    return panel;
  }

  /** « Durée maximale d’un travail » (SPEC-EQUIPE §2): minutes, empty = the engine's own. */
  function advancedSection() {
    const minutes = input({
      type: 'number', value: form.timeoutMin, min: String(LIMITS.timeoutMin), max: String(LIMITS.timeoutMax), step: '1',
      inputmode: 'numeric', placeholder: t('ad.f.timeoutPlaceholder'), class: 'ad-timeout-input',
    });
    minutes.addEventListener('input', () => { form.timeoutMin = minutes.value; fields.timeoutMin.setError(null); sync(); });
    const row = h('div', { class: 'ad-timeout-row' }, minutes, h('span', { class: 'ad-timeout-unit muted' }, t('ad.f.timeoutUnit')));
    row.input = minutes; // field() labels the input, not the row
    fields.timeoutMin = field(t('ad.f.timeout'), row, { hint: t('ad.f.timeoutHint') });
    return sectionEl(t('ad.set.advanced'), fields.timeoutMin);
  }

  // --- lessons (saved at once, not part of the form) --------------------------------------------
  const lessonList = h('ol', { class: 'ad-lessons' });
  const lessonCount = h('span', { class: 'ad-count' });
  const lessonInput = input({ placeholder: t('ad.lessons.placeholder'), maxLength: LIMITS.lesson + 50, 'aria-label': t('ad.lessons.addLabel') });
  const lessonAdd = button(t('ad.lessons.add'), { variant: 'secondary', size: 'sm', icon: 'plus', type: 'submit' });
  const lessonRow = h('div', { class: 'ad-lesson-row' }, lessonInput, lessonAdd);
  lessonRow.input = lessonInput; // field() labels the input, not the row
  const lessonField = field(t('ad.lessons.addLabel'), lessonRow);
  let lessonBusy = false;

  function lessonsOf() {
    return Array.isArray(agent.lessons) ? agent.lessons.filter((l) => typeof l === 'string') : [];
  }

  function renderLessons() {
    const lessons = lessonsOf();
    setText(lessonCount, t('ad.lessons.count', { n: lessons.length, max: LIMITS.lessons }));
    if (!lessons.length) {
      lessonList.replaceChildren(h('li', { class: 'ad-lesson is-empty' }, t('ad.lessons.empty')));
      return;
    }
    lessonList.replaceChildren(...lessons.map((text, index) => h('li', { class: 'ad-lesson' },
      h('span', { class: 'ad-lesson-text' }, text),
      iconButton('close', t('ad.lessons.delete', { text: text.length > 60 ? `${text.slice(0, 59)}…` : text }), {
        variant: 'ghost', size: 14, disabled: lessonBusy, onClick: () => removeLesson(index, text),
      }))));
  }

  function lessonsFromResponse(res) {
    if (res && typeof res === 'object' && Array.isArray(res.lessons)) {
      agent = { ...agent, lessons: res.lessons };
      storeAgent(ctx, res);
      return true;
    }
    return false;
  }

  async function addLesson(e) {
    e?.preventDefault?.();
    if (lessonBusy) return;
    const { value, error } = validateLesson(lessonInput.value, lessonsOf());
    if (error) { lessonField.setError(error); lessonInput.focus?.(); return; }
    lessonField.setError(null);
    lessonBusy = true;
    lessonAdd.disabled = true;
    try {
      const res = await ctx.api.post(`${path}/lessons`, { text: value });
      if (destroyed) return;
      if (!lessonsFromResponse(res)) agent = { ...agent, lessons: [...lessonsOf(), value] };
      lessonInput.value = '';
      ctx.toast?.success?.(t('ad.lessons.added'));
    } catch (err) {
      if (!destroyed) lessonField.setError(err?.message || t('common.error'));
    } finally {
      lessonBusy = false;
      lessonAdd.disabled = false;
      if (!destroyed) renderLessons();
    }
  }

  async function removeLesson(index, text) {
    if (lessonBusy) return;
    lessonBusy = true;
    renderLessons();
    try {
      // The index must still point at this text: reload first when the list changed meanwhile.
      if (lessonsOf()[index] !== text) throw new Error(t('ad.changedElsewhere'));
      const res = await ctx.api.del(`${path}/lessons/${encodeURIComponent(String(index))}`);
      if (destroyed) return;
      if (!lessonsFromResponse(res)) agent = { ...agent, lessons: lessonsOf().filter((_, i) => i !== index) };
      ctx.toast?.success?.(t('ad.lessons.deleted'));
    } catch (err) {
      if (!destroyed) ctx.toast?.error?.(err);
    } finally {
      lessonBusy = false;
      if (!destroyed) renderLessons();
    }
  }

  function lessonsSection() {
    const form2 = h('form', { class: 'ad-lesson-form', novalidate: true, onSubmit: addLesson }, lessonField);
    renderLessons();
    return sectionEl(t('ad.set.lessons'), h('p', { class: 'ad-set-intro' }, t('ad.lessons.intro'), ' ', lessonCount), lessonList, form2);
  }

  function dangerSection() {
    if (!isAdmin) return null;
    return sectionEl(t('ad.set.danger'), h('div', { class: 'ad-danger' },
      h('p', { class: 'ad-danger-text' }, t('ad.deleteHint')),
      button(t('ad.delete'), { variant: 'danger', size: 'sm', icon: 'close', onClick: () => removeAgent() })));
  }

  // --- rendering ---------------------------------------------------------------------------------
  function render() {
    if (destroyed) return;
    for (const k of Object.keys(fields)) if (k !== 'connectors') delete fields[k];
    const engineSec = engineSection(() => renderConnectors());
    renderConnectors();
    replaceChildren(body,
      identity(),
      briefSection(),
      engineSec,
      autonomySection(),
      namedSection('connectors', t('ad.set.connectors'), connBox),
      lessonsSection(),
      advancedSection(),
      dangerSection(),
    );
    setGeneral([]);
    sync();
  }

  function resetFrom(next) {
    agent = next;
    original = formFromAgent(agent);
    form = structuredClone(original);
    changedElsewhere = false;
    render();
    if (pendingFocus) { const key = pendingFocus; pendingFocus = null; focusField(key); }
    if (pendingSection) { const key = pendingSection; pendingSection = null; showSection(key); }
  }

  /**
   * Brings a section into view and gives it the focus (`connectors`), once the agent is loaded.
   * @param {string} key
   */
  function showSection(key) {
    if (destroyed) return;
    if (!loaded) { pendingSection = key; load(); return; }
    const sec = [...body.querySelectorAll('section')].find((n) => n.dataset?.sec === key);
    if (!sec) return;
    sec.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    sec.focus?.({ preventScroll: true });
  }

  /**
   * Puts the caret in a field of the form (`name`, `role`, `does`, `brief`, `model`…), once the
   * agent is loaded (the request is remembered until then).
   * @param {string} key
   */
  function focusField(key) {
    if (destroyed) return;
    if (!loaded) { pendingFocus = key; load(); return; }
    const ctl = fields[key]?.querySelector?.('input, textarea, select');
    if (!ctl) return;
    ctl.focus?.();
    try { ctl.select?.(); } catch { /* not a text control */ }
  }

  async function load() {
    if (loading || destroyed) return;
    loading = true;
    try {
      const res = await ctx.api.get(path);
      if (destroyed) return;
      if (res && typeof res === 'object' && res.id === id) agent = { ...agent, ...res };
    } catch (err) {
      if (destroyed) return;
      if (err?.status === 403) {
        body.replaceChildren(emptyState({ icon: 'shield', title: t('ad.settings.forbidden') }));
        loading = false;
        return;
      }
      if (err?.status !== 404) ctx.toast?.error?.(err);
    }
    loading = false;
    loaded = true;
    resetFrom(agent);
  }

  function discard() {
    resetFrom(agent);
    ctx.toast?.info?.(t('ad.clean'));
  }

  async function save() {
    if (saving || destroyed) return;
    const errors = validateAgentForm(form);
    showErrors(errors);
    setGeneral([]);
    const bad = FIELD_ORDER.find((k) => errors[k]);
    if (bad) {
      ctx.toast?.warn?.(t('ad.invalid'));
      fields[bad]?.querySelector?.('input, textarea, select')?.focus?.();
      return;
    }
    const body0 = patch();
    if (!Object.keys(body0).length) return;
    saving = true;
    sync();
    try {
      const res = await ctx.api.patch(path, body0);
      if (destroyed) return;
      const next = res && typeof res === 'object' && res.id === id ? { ...agent, ...res } : { ...agent, ...body0 };
      storeAgent(ctx, next);
      saving = false;
      resetFrom(next);
      ctx.toast?.success?.(t('ad.saved', { name: next.name || agent.name }));
    } catch (err) {
      if (destroyed) return;
      saving = false;
      if (err?.status === 400) {
        const p = problemsFromDetails(err.details, err.message);
        showErrors(p.fields);
        setGeneral(p.general);
        ctx.toast?.error?.(t('ad.invalid'));
      } else {
        ctx.toast?.error?.(err);
      }
      sync();
    }
  }

  async function removeAgent() {
    const ok = await ctx.overlay.confirm({
      title: t('ad.deleteTitle', { name: agent.name }), message: t('ad.deleteText'), confirmLabel: t('ad.deleteOk'), danger: true,
    });
    if (!ok || destroyed) return;
    try {
      await ctx.api.del(path);
      ctx.toast?.success?.(t('ad.deleted', { name: agent.name }));
      const office = ctx.store.get().office;
      ctx.store.set({ office: { ...office, agents: office.agents.filter((a) => a.id !== id) } });
      ctx.refresh?.office?.();
      onDeleted?.();
    } catch (err) {
      if (!destroyed) ctx.toast?.error?.(err);
    }
  }

  // The agent changed elsewhere (bootstrap reload after another manager's save): follow it when
  // nothing is being edited, otherwise keep the user's changes and say so.
  if (ctx.store.on) {
    offs.push(ctx.store.on('office', (office) => {
      if (!loaded || saving || destroyed) return;
      const next = office?.agents?.find((a) => a.id === id);
      if (!next) return;
      const incoming = formFromAgent({ ...agent, ...next });
      const lessonsChanged = Array.isArray(next.lessons) && JSON.stringify(next.lessons) !== JSON.stringify(lessonsOf());
      if (JSON.stringify(incoming) === JSON.stringify(original)) {
        if (lessonsChanged) { agent = { ...agent, lessons: next.lessons }; renderLessons(); }
        return;
      }
      if (!Object.keys(patch()).length) resetFrom({ ...agent, ...next });
      else { changedElsewhere = true; agent = { ...agent, ...next }; original = incoming; sync(); }
    }));
    offs.push(ctx.store.on('connectors', () => { if (loaded && !destroyed) renderConnectors(); }));
    offs.push(ctx.store.on('engines', () => { if (loaded && !destroyed && !Object.keys(patch()).length) render(); }));
  }

  sync();

  return {
    el,
    footer,
    activate() { if (!loaded) load(); },
    deactivate() {},
    focusField,
    showSection,
    isDirty: () => loaded && Object.keys(patch()).length > 0,
    destroy() {
      destroyed = true;
      for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
    },
  };
}

/** CSS.escape when available (browser), else a conservative fallback for server keys ([A-Za-z0-9_-]). */
function CSS_ESCAPE(s) {
  if (typeof globalThis.CSS?.escape === 'function') return globalThis.CSS.escape(s);
  return String(s).replace(/[^A-Za-z0-9_-]/g, (c) => `\\${c}`);
}
